/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { message, Pagination, Progress, Table } from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import React, { Fragment, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import IconButton from '~components/_common/IconButton/IconButton';
import {
  ClassicRegistration,
  downloadClassicRegistrationReport,
  getClassicRegistrations,
  GetClassicRegistrationsParams,
} from '~src/services/api/modules';
import { apiCweb } from '~src/services/api/api-cweb';
import { runParallelJsonFetches } from '~src/utils/parallelFetch';
import { styleFn } from './FileTable.style';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { SearchContext } from '~src/contexts/SearchContext';
import { TableEntry } from './FileTable.utils';
import { TableProps } from 'antd/lib/table';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { useStore } from '~src/store';
import { useProjectId } from '~src/hooks/useProjectId';
import { appLog } from '~src/utils/log';
import Viewer from '~components/atoms/Viewers/Viewer';
import TasksDa from '~src/assets/nav-icons/tasks-da.png';
import TasksEn from '~src/assets/nav-icons/tasks-en.png';
import EnquieriesDa from '~src/assets/nav-icons/enquieries-da.png';
import EnquieriesEn from '~src/assets/nav-icons/enquieries-en.png';
import SupervisionDa from '~src/assets/nav-icons/supervision-da.png';
import SupervisionEn from '~src/assets/nav-icons/supervision-en.png';
import ControlDa from '~src/assets/nav-icons/control-da.png';
import ControlEn from '~src/assets/nav-icons/control-en.png';
import Comissioning from '~src/assets/nav-icons/commissioning-en.png';
import EconomicsDa from '~src/assets/nav-icons/economics-da.png';
import EconomicsEn from '~src/assets/nav-icons/economics-en.png';
import FacilityManagementDa from '~src/assets/nav-icons/facility-management-da.png';
import FacilityManagementEn from '~src/assets/nav-icons/facility-management-en.png';
import { MODULES } from '~src/types/enums';
import { LANGUAGE } from '~src/config';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';

// Helper function to get module icon based on module ID and language
const getModuleIcon = (moduleId: number): string => {
  switch (moduleId) {
    case MODULES.TASK:
      return LANGUAGE.startsWith('da') ? TasksDa : TasksEn;
    case MODULES.TQ:
      return LANGUAGE.startsWith('da') ? EnquieriesDa : EnquieriesEn;
    case MODULES.SUPV:
      return LANGUAGE.startsWith('da') ? SupervisionDa : SupervisionEn;
    case MODULES.CONT:
      return LANGUAGE.startsWith('da') ? ControlDa : ControlEn;
    case MODULES.COM:
      return Comissioning;
    case MODULES.ECO:
      return LANGUAGE.startsWith('da') ? EconomicsDa : EconomicsEn;
    case MODULES.DV:
      return LANGUAGE.startsWith('da')
        ? FacilityManagementDa
        : FacilityManagementEn;
    default:
      // Fallback to empty for unknown modules (will use FontAwesome question-square)
      return '';
  }
};

export interface SectorRegistrationsTableProps extends TableProps<TableEntry> {
  sectorId?: number | string;
  moduleId?: number;
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  highlightedRow?: TableEntry;
  onRow?: (record: TableEntry, index?: number) => any;
}

export const SectorRegistrationsTable: React.FC<SectorRegistrationsTableProps> = ({
  sectorId,
  moduleId,
  selectedKeys,
  onSelect,
  onSelectAll,
  highlightedRow,
  onRow: handleRow,
  ...tableProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const { setFileToView } = useFileViewerContext();
  const { moduleFilterStates, registrationFileFilter } = useStore();
  const searchContext = useContext(SearchContext);
  const projectId = useProjectId();

  // Preview drawer state
  const [previewDrawer, setPreviewDrawer] = React.useState<{
    visible: boolean;
    registration?: ClassicRegistration;
    fileUrl?: string;
  }>({ visible: false });

  // Cleanup blob URL when it changes or component unmounts
  React.useEffect(() => {
    const currentUrl = previewDrawer.fileUrl;
    return () => {
      if (currentUrl && currentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [previewDrawer.fileUrl]);

  // Fetch URL when drawer opens
  React.useEffect(() => {
    if (
      previewDrawer.visible &&
      previewDrawer.registration &&
      !previewDrawer.fileUrl
    ) {
      appLog('Preview drawer opened, fetching registration report', {
        registration: previewDrawer.registration,
        project_id: previewDrawer.registration.project_id,
        id: previewDrawer.registration.id,
      });

      downloadClassicRegistrationReport({
        projectId: String(previewDrawer.registration.project_id),
        id: previewDrawer.registration.id,
        response_type: 'stream',
      })
        .then((blob) => {
          appLog('Preview blob received successfully', {
            size: blob.size,
            type: blob.type,
          });
          const url = URL.createObjectURL(blob);
          appLog('Preview blob URL created', { url });
          setPreviewDrawer((prev) => ({ ...prev, fileUrl: url }));
        })
        .catch((err) => {
          appLog('Preview error - failed to fetch or create blob URL', {
            error: err,
            errorMessage: err?.message,
            errorResponse: err?.response,
            status: err?.response?.status,
            statusText: err?.response?.statusText,
          });
          const errorMsg =
            err?.response?.status === 400
              ? 'This registration does not have a preview available'
              : 'Failed to load preview';
          message.error(errorMsg);
          setPreviewDrawer({ visible: false });
        });
    }
  }, [
    previewDrawer.visible,
    previewDrawer.registration,
    previewDrawer.fileUrl,
  ]);

  // State for accumulated registrations, loaded eagerly in batches on mount
  const [
    accumulatedRegistrations,
    setAccumulatedRegistrations,
  ] = React.useState<ClassicRegistration[]>([]);
  const [isLoadingRegistrations, setIsLoadingRegistrations] = React.useState(
    false
  );
  // True only while the remaining batches are still being fetched in the
  // background (after the first page-worth of data is already visible).
  // Used to keep the pagination controls disabled so users can't jump to a
  // page whose data hasn't loaded yet.
  const [isBackgroundLoading, setIsBackgroundLoading] = React.useState(false);
  // Total item count reported by the server (from the `total-count` header of
  // the first response). Used to pin the pagination's total page count
  // immediately, instead of it growing as background batches complete.
  const [totalCount, setTotalCount] = React.useState(0);

  // Background batch size: default table page is 50 rows, so 200 gives us a buffer
  // of ~4 pages loaded ahead while keeping each individual request small/fast.
  const BATCH_SIZE = 200;
  // How many batches to fetch concurrently (via Web Workers) once we know how many
  // are left. Mirrors the parallel-worker pattern used for file downloads.
  const NUM_PARALLEL_WORKERS = 4;

  // Eagerly load ALL registrations for this sector/module on mount (and when it changes).
  // Strategy:
  //  1. Fetch the first batch (200) normally - this lets the table render fast and
  //     tells us the total item count.
  //  2. If there's more data, fetch all remaining batches of 200 in parallel using a
  //     small pool of Web Workers (instead of sequentially awaiting one at a time),
  //     which meaningfully speeds up loading large lists (e.g. 7000+ items).
  React.useEffect(() => {
    let cancelled = false;

    if (!projectId || (sectorId === undefined && moduleId === undefined)) {
      setAccumulatedRegistrations([]);
      setIsLoadingRegistrations(false);
      setTotalCount(0);
      return;
    }

    const baseParams: Omit<GetClassicRegistrationsParams, 'skip' | 'take'> = {
      projectId: String(projectId),
      // Use sector_ids if sectorId provided, otherwise use modules parameter
      ...(sectorId !== undefined
        ? {
            sector_ids:
              sectorId === 'outside' || sectorId === 'null'
                ? 'null'
                : String(sectorId),
          }
        : { modules: String(moduleId) }),
    };

    const loadAllRegistrations = async () => {
      setIsLoadingRegistrations(true);
      setIsBackgroundLoading(false);
      setAccumulatedRegistrations([]);
      setTotalCount(0);

      try {
        // Step 1: fetch the first batch normally so the table can render quickly
        // and we learn the total count.
        const first = await getClassicRegistrations({
          ...baseParams,
          skip: 0,
          take: BATCH_SIZE,
        });

        if (cancelled) return;

        setAccumulatedRegistrations([...first.data]);
        // We already have data to show - drop the full-table spinner now instead
        // of waiting for every background batch to finish loading.
        setIsLoadingRegistrations(false);

        appLog('Loaded registrations batch', {
          skip: 0,
          batchLength: first.data.length,
          totalCount: first.totalCount,
        });

        // Pin the pagination's total count now so the full page count shows
        // immediately, instead of growing as background batches complete.
        const serverTotalCount = first.totalCount || first.data.length;
        setTotalCount(serverTotalCount);

        if (
          serverTotalCount > first.data.length &&
          first.data.length === BATCH_SIZE
        ) {
          // Step 2: fetch the remaining batches in parallel via a Worker pool.
          // Keep the pagination controls disabled while this runs, so users
          // can't jump to a page whose data hasn't loaded yet.
          setIsBackgroundLoading(true);

          const remainingSkips: number[] = [];
          for (
            let skip = BATCH_SIZE;
            skip < serverTotalCount;
            skip += BATCH_SIZE
          ) {
            remainingSkips.push(skip);
          }

          const query = new URLSearchParams();
          Object.entries(baseParams).forEach(([key, value]) => {
            if (key !== 'projectId' && value !== undefined) {
              query.set(key, String(value));
            }
          });
          query.set('sort', 'desc(created_at)');
          query.set('take', String(BATCH_SIZE));

          const baseUrl = `${apiCweb.defaults.baseURL}/projects/${projectId}/classic_registrations`;
          const authHeader =
            (apiCweb.defaults.headers.common.Authorization as string) || '';

          const tasks = remainingSkips.map((skip) => ({
            url: `${baseUrl}?${query.toString()}&skip=${skip}`,
            headers: { Authorization: authHeader },
          }));

          // Keep results ordered by skip so pagination shows a stable order,
          // even though batches can complete out of order.
          const batchesBySkip = new Map<number, ClassicRegistration[]>();

          await runParallelJsonFetches<ClassicRegistration[]>({
            tasks,
            numWorkers: NUM_PARALLEL_WORKERS,
            onResult: ({ index, data }) => {
              if (cancelled) return;
              batchesBySkip.set(remainingSkips[index], data);

              const merged = [...first.data];
              remainingSkips.forEach((skip) => {
                const batch = batchesBySkip.get(skip);
                if (batch) merged.push(...batch);
              });
              setAccumulatedRegistrations(merged);

              appLog('Loaded registrations batch (parallel)', {
                skip: remainingSkips[index],
                batchLength: data.length,
                totalLoaded: merged.length,
                totalCount: serverTotalCount,
              });
            },
            onError: (index, error) => {
              appLog('Failed to load registrations batch (parallel)', {
                skip: remainingSkips[index],
                error,
              });
            },
          });
        }
      } catch (err) {
        if (!cancelled) {
          appLog('Failed to load registrations', err);
          message.error(
            intl.formatMessage({ id: 'registrations-load-failed' })
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRegistrations(false);
          setIsBackgroundLoading(false);
        }
      }
    };

    loadAllRegistrations();

    return () => {
      cancelled = true;
    };
  }, [projectId, sectorId, moduleId]);

  // Transform registrations to TableEntry format
  const registrationsSource = useMemo(
    () =>
      accumulatedRegistrations.map((reg, index) => {
        const moduleIcon = getModuleIcon(reg.module);
        return {
          key: String(reg.id),
          id: index, // CRITICAL: index from accumulatedRegistrations gives unique IDs (0, 1, 2... across ALL batches)
          name: reg.filename || '',
          type: moduleIcon
            ? {
                customIcon: moduleIcon,
                icon: '',
                iconStyle: '',
                color: 'blue',
              }
            : {
                icon: 'file-pdf',
                iconStyle: '',
                color: 'red',
              },
          description: reg.workgroup_name || '',
          sector: reg.sector_name || '',
          recipient: reg.created_by_name || '',
          subject: `${reg.user_initials} - ${reg.workgroup_initials}`,
          date: reg.created_at || 0,
          createdAt: reg.created_at || 0,
          updatedAt: reg.created_at || 0,
          extension: 'pdf',
          favourite: false,
          disabled: false,
          // Add required TableEntry fields
          size: 0,
          state: reg.state || 0,
          permission: 0,
          dbFile: undefined,
          dbFolder: undefined,
          folder: false,
          moduleFile: false,
          parentId: String(sectorId),
          version: null,
          isFolder: false,
          isFile: true,
          classicRegistration: reg,
          // Pre-computed, lowercased search haystack covering every field the
          // header's global search should match against (name/title, but also
          // workgroup, sector, created-by and initials). Built once per item
          // here instead of re-lowercasing several fields on every keystroke.
          _searchHaystack: [
            reg.filename,
            (reg as any).title,
            reg.workgroup_name,
            reg.sector_name,
            reg.created_by_name,
            reg.user_initials,
            reg.workgroup_initials,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        } as TableEntry & {
          classicRegistration: ClassicRegistration;
          _searchHaystack: string;
        };
      }),
    [accumulatedRegistrations, sectorId]
  );

  const filteredSource = useMemo(() => {
    // Lowercase the query once up-front instead of on every field/item check.
    const query = searchContext.query.trim().toLowerCase();

    return registrationsSource.filter((item) => {
      // Get the module from classicRegistration
      const itemModuleId = (item as any).classicRegistration?.module;

      // IMPORTANT: Only apply module filter when viewing by sector
      // When viewing individual module, API already filtered by module
      if (sectorId !== undefined) {
        // Apply module filter for sector-based views
        if (itemModuleId && !moduleFilterStates.includes(itemModuleId)) {
          return false;
        }
      }
      // For moduleId-based views, skip module filter (already filtered by API)

      // Apply file filter
      if (registrationFileFilter === 'files-only') {
        const hasFile = !!(item as any).classicRegistration?.filename;
        if (!hasFile) {
          return false;
        }
      }

      if (!query) return true;

      // Match against the pre-computed haystack (name/title + workgroup +
      // sector + created-by + initials), so searching "Hansen" finds rows by
      // the "Created By" field too, not just name/title.
      return (item as any)._searchHaystack.includes(query);
    });
  }, [
    registrationsSource,
    sectorId,
    moduleFilterStates,
    registrationFileFilter,
    searchContext.query,
  ]);

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 75,
        align: 'center',
        render: (type, record) => {
          if (type.customIcon) {
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <NavIcon
                  icon={type.customIcon}
                  customStyle={{ height: '20px', width: 'auto' }}
                />
              </div>
            );
          }
          return (
            <IconButton
              iconStyle={type.iconStyle}
              icon={type.icon}
              customStyle={styles.icons}
              color={type.color}
              tooltip={intl.formatMessage(
                { id: type.icon },
                { type: record.extension?.toUpperCase() }
              )}
              badge={type.is_empty && '0'}
              backgroundOff
            />
          );
        },
      },
      {
        title: intl.formatMessage({ id: 'action' }),
        width: 72,
        align: 'center',
        dataIndex: 'type',
        render: (type, record) => {
          const hasFile = !!(record as any).classicRegistration?.filename;
          return (
            <IconButton
              click={() => {
                if (!hasFile) return; // Prevent action
                setPreviewDrawer({
                  visible: true,
                  registration: (record as any).classicRegistration,
                });
              }}
              iconStyle={type.iconStyle}
              icon={['far', 'eye']}
              color={hasFile ? 'red' : 'gray'}
              disabled={!hasFile}
            />
          );
        },
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 250,
        ellipsis: false,
        render: (name, record) => {
          const registration = (record as any).classicRegistration;
          const title = registration?.title || '';
          const filename = registration?.filename || '';
          const moduleId = registration?.module;

          // For Tasks module (module = 1), title and filename are the same
          // Only show title in this case to avoid duplication
          const isTasksModule = moduleId === MODULES.TASK;
          const shouldShowBoth =
            filename && filename !== title && !isTasksModule;

          return (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {title && <div style={{ fontWeight: 500 }}>{title}</div>}
              {shouldShowBoth && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {filename}
                </div>
              )}
              {record.favourite && (
                <IconButton
                  icon={['fas', 'star']}
                  color="yellow"
                  backgroundOff
                />
              )}
            </div>
          );
        },
      },
      {
        title: intl.formatMessage({ id: 'workgroup' }),
        dataIndex: 'description',
        sorter: (a, b) => {
          if (!a.description && !b.description) return 0;
          if (!a.description) return -1;
          if (!b.description) return 1;
          return (a.description || '').localeCompare(b.description || '');
        },
        width: 200,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'sector' }),
        dataIndex: 'sector',
        width: 100,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'created-by' }),
        dataIndex: 'recipient',
        width: 100,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'initials' }),
        dataIndex: 'subject',
        width: 100,
        ellipsis: true,
      },
    ],
    [intl]
  );

  // Controlled pagination state so we can block navigation to pages that
  // haven't loaded yet, while still allowing free navigation within the pages
  // we already have data for (e.g. the first 200 items = first 4 pages @ 50/page).
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(50);

  // Reset to page 1 whenever we start loading a different sector/module, or
  // whenever the active search query changes (otherwise the user could be
  // stuck on a page number that no longer has any matching rows).
  React.useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId, moduleId, searchContext.query]);

  // How many full pages of data we actually have loaded so far. This grows in
  // real time as background batches complete, progressively unlocking pages.
  const loadedPages = Math.max(
    1,
    Math.floor(accumulatedRegistrations.length / pageSize)
  );

  // Pin the pagination total to the server-reported count so the full page
  // count (e.g. ~140 pages for 6986 items) shows immediately, instead of
  // growing as background batches complete. If search/module/file filters
  // are actively excluding rows, fall back to the real filtered count instead,
  // since the server total would then overstate what's actually visible.
  const isFiltered = filteredSource.length !== registrationsSource.length;
  const pinnedTotal = isFiltered
    ? filteredSource.length
    : totalCount || filteredSource.length;

  // We render pagination ourselves (instead of via the Table's built-in
  // `pagination` prop) so we can place the loading-progress bar in the same
  // row, to the left of the pager. That means we need to slice the current
  // page's rows out of `filteredSource` manually.
  const paginatedSource = useMemo(
    () =>
      filteredSource.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      ),
    [filteredSource, currentPage, pageSize]
  );

  // The footer row (progress bar + pagination) added below the table needs its
  // own vertical space - otherwise the table body height calculated by the
  // parent (`scroll.y`) leaves no room for it, pushing it below the visible
  // viewport / behind the details panel at the bottom of the screen.
  const PAGINATION_RESERVED_HEIGHT = 72;
  const { scroll: incomingScroll, ...restTableProps } = tableProps;
  const effectiveScroll = incomingScroll?.y
    ? {
        ...incomingScroll,
        y:
          typeof incomingScroll.y === 'number'
            ? incomingScroll.y - PAGINATION_RESERVED_HEIGHT
            : `calc((${incomingScroll.y}) - ${PAGINATION_RESERVED_HEIGHT}px)`,
      }
    : incomingScroll;

  return (
    <div>
      <Table<TableEntry>
        showSorterTooltip={false}
        css={[
          styles.table,
          effectiveScroll?.y && {
            '.ant-table-body': {
              height: effectiveScroll.y,
            },
          },
        ]}
        className="main-content-window"
        scroll={effectiveScroll}
        pagination={false}
        bordered
        columns={columns}
        dataSource={paginatedSource}
        loading={isLoadingRegistrations}
        rowClassName={(record) => {
          let className = '';
          if (record.key === highlightedRow?.key) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (record.disabled) {
            className += 'unfinished' + ' ';
          }

          return className;
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedKeys,
          columnWidth: 50,
          onSelect,
          onSelectAll,
          getCheckboxProps: (record) => ({
            disabled: !(record as any).classicRegistration?.filename,
          }),
        }}
        onRow={(record, index) => handleRow?.(record, index)}
        {...restTableProps}
      />

      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '12px 16px',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
        }}>
        <div css={{ flex: 1, minWidth: 0 }}>
          {isBackgroundLoading && (
            <Progress
              css={{ maxWidth: 420 }}
              percent={
                totalCount > 0
                  ? Math.min(
                      100,
                      Math.round(
                        (accumulatedRegistrations.length / totalCount) * 100
                      )
                    )
                  : 0
              }
              size="small"
              status="active"
              showInfo
              format={(percent) =>
                intl.formatMessage(
                  { id: 'loading-more-registrations' },
                  {
                    loaded: accumulatedRegistrations.length,
                    total: totalCount,
                    percent,
                  }
                )
              }
            />
          )}
        </div>

        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={pinnedTotal}
          // Block the page-size picker while background loading is in progress -
          // changing page size while more data is still streaming in would make
          // the "how many pages are loaded" math unreliable.
          showSizeChanger={!isBackgroundLoading}
          pageSizeOptions={['50', '100', '200', '500']}
          onChange={(page, size) => {
            // Guard against jumping to a page we don't have data for yet
            // (covers the Next/Jump-next arrows too, not just page numbers).
            if (isBackgroundLoading && page > loadedPages) {
              return;
            }
            setCurrentPage(page);
            if (size) setPageSize(size);
          }}
          onShowSizeChange={(current, size) => {
            setCurrentPage(current);
            setPageSize(size);
          }}
          itemRender={(page, type, originalElement) => {
            if (isBackgroundLoading && type === 'page' && page > loadedPages) {
              return (
                <span
                  style={{
                    cursor: 'not-allowed',
                    opacity: 0.35,
                    pointerEvents: 'none',
                  }}>
                  {page}
                </span>
              );
            }
            return originalElement;
          }}
          showTotal={(total) =>
            isBackgroundLoading
              ? intl.formatMessage({ id: 'total-items-loading' }, { total })
              : intl.formatMessage({ id: 'total-items' }, { total })
          }
        />
      </div>

      {previewDrawer.visible && previewDrawer.registration && (
        <Viewer
          mode="drawer"
          drawerVisible={previewDrawer.visible}
          onDrawerClose={() => {
            setPreviewDrawer({ visible: false });
          }}
          drawerTitle={previewDrawer.registration.filename || 'Preview'}
          drawerWidth={800}
          fileUrl={previewDrawer.fileUrl || ''}
          fileExtension="pdf"
          isLoadingUrl={!previewDrawer.fileUrl}
        />
      )}
    </div>
  );
};
